/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';

import { useSuggestionCardPositioning } from '../hooks/useSuggestionCardPositioning';

import { ScrollBackToSuggestionButton } from './SuggestionCard/ScrollBackToSuggestionButton';

const styles = cssMap({
	targetAnchoring: {
		display: 'block',
		position: 'absolute',
		insetBlockStart: `anchor(start)`,
		insetInlineStart: `anchor(end)`,
	},
	cardContainer: {
		zIndex: 510, // same as akEditorFloatingDialogZIndex
		positionVisibility: 'anchors-valid',
		transitionProperty: 'transform, opacity',
		transitionDuration: token('motion.duration.long', '0.3s'),
		transitionTimingFunction: token('motion.easing.inout.bold', 'ease-in-out'),
	},
	// Hide the card until the positioning hook has measured the target, so the fade-in has a
	// clean starting state.
	preMeasurement: {
		opacity: 0,
	},
	// Applied once `isMeasured` flips, so the card fades in from its final position.
	fadeIn: {
		animationName: token('motion.keyframe.fade.in'),
		animationDuration: token('motion.duration.short'),
		animationTimingFunction: token('motion.easing.out.practical'),
		animationFillMode: 'backwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	scrollBackContainer: {
		width: '280px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'fixed',
	},
	scrollBackTop: { top: token('space.300'), animation: token('motion.popup.enter.bottom') },
	scrollBackBottom: { bottom: token('space.300'), animation: token('motion.popup.enter.top') },
	// Longhand animation overrides must come AFTER the `animation` shorthand entries above
	// (scrollBackTop/scrollBackBottom) in the `css={[...]}` array so the @compiled
	// shorthand-property-sorting rule sees shorthand-before-longhand across all entries.
	scrollBackAnimation: {
		animationDelay: token('motion.duration.xxlong'),
		animationFillMode: 'both',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	// As `scrollBackAnimation`, minus the hand-off delay: used when the target was
	// already out of view on select, so there is no card sliding away to wait for.
	scrollBackAnimationQuick: {
		animationDelay: token('motion.duration.xxshort'),
		animationFillMode: 'both',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	scrollBackRight: {
		// gutter + object actions container width + gutter
		right: `calc(${token('space.300')} + ${token('space.600')} + ${token('space.300')})`,
	},
	pinnedBottomRight: {
		position: 'fixed',
		bottom: token('space.300'),
		// gutter + object actions container width + gutter
		right: `calc(${token('space.300')} + ${token('space.600')} + ${token('space.300')})`,
	},
	anchoredToTargetEnd: {
		position: 'absolute',
		// gutter + object actions container width + gutter
		right: `calc(${token('space.300')} + ${token('space.600')} + ${token('space.300')})`,
	},
	pinnedTop: {
		position: 'fixed',
		top: token('space.300'),
	},
	pinnedBottom: {
		position: 'fixed',
		bottom: token('space.300'),
	},
	anchoredToTarget: {
		position: 'absolute',
	},
	slideUp: {
		opacity: 0,
		transform: `translateY(calc(-100% - ${token('space.300')} - ${token('space.200')}))`,
		transitionProperty: 'transform, opacity',
		transitionDuration: token('motion.duration.long'),
		transitionTimingFunction: token('motion.easing.out.practical'),
		transitionDelay: `${token('motion.duration.medium')}, ${token('motion.duration.xxlong')}`,
	},
	slideDown: {
		opacity: 0,
		transform: `translateY(calc(100% + ${token('space.300')} + ${token('space.200')}))`,
		transitionProperty: 'transform, opacity',
		transitionDuration: token('motion.duration.long'),
		transitionTimingFunction: token('motion.easing.out.practical'),
		transitionDelay: `${token('motion.duration.medium')}, ${token('motion.duration.xxlong')}`,
	},
	// The card stays mounted — the positioning hook measures it — but is never shown
	// when the target was already out of view on select. No transition, so it can't
	// flash on the way to hidden.
	hiddenForScrollBack: {
		opacity: 0,
		pointerEvents: 'none',
		transitionDuration: token('motion.duration.instant'),
	},
});

export type AnchoredCardAnchorNames = {
	/**
	 * Anchor sitting in the right gutter of the target's doc-level block, supplying the
	 * card's inline (horizontal) position.
	 */
	base: string;
	/** Anchor at the end of the target, used to detect when it leaves the viewport. */
	end: string;
	/** Anchor at the start of the target, supplying the card's block (vertical) position. */
	start: string;
};

type AnchoredCardProps = {
	anchorNames: AnchoredCardAnchorNames;
	/**
	 * Must point at the card element passed as `children` — its measured size feeds the viewport
	 * calculations.
	 */
	cardRef: RefObject<HTMLDivElement>;
	children: React.ReactNode;
	/**
	 * Called when the scroll-back pill shown after the target scrolls out of view is clicked.
	 * Omit to render no pill — the card still slides away with the target.
	 */
	onScrollBack?: () => void;
	/**
	 * Identifies the anchor target; changing it re-seeds the measurement. Must track whatever
	 * `anchorNames` is derived from rather than the card's own identity: a card can outlive an
	 * edit that moves its anchors to another node, and since the anchor spans are reused across
	 * that change nothing else would re-measure. No measuring happens while undefined.
	 */
	targetId?: string;
	view?: EditorView;
};

/**
 * Positions a card against the CSS anchors emitted (as ProseMirror widget decorations) at
 * a target node, keeping it next to that node as the window resizes and the content
 * reflows, and pinning it to the viewport edge when the target scrolls out of view.
 *
 * Shared by the suggestion card and the follow-up card that replaces it, which is how the
 * follow-up card lands in exactly the position the removed card occupied.
 *
 * Pass a React `key` that changes per card (e.g. the selected suggestion's id): the fade-in is a
 * CSS animation, so it only fires once per mount. Remounting re-seeds the measurement in the
 * same commit, before paint, so there is no unpositioned frame.
 */
export const AnchoredCard = ({
	anchorNames,
	cardRef,
	children,
	onScrollBack,
	targetId,
	view,
}: AnchoredCardProps): JSX.Element => {
	/**
	 * State-backed refs, so the card mounts correctly after its anchor does. Start and end are
	 * separate spans (top and bottom of the block) so we can tell when the target leaves the
	 * editor viewport.
	 */
	const [targetStart, setTargetStart] = useState<HTMLSpanElement>();
	const targetStartRef = useCallback((node: HTMLSpanElement) => {
		setTargetStart(node);
	}, []);
	const [targetEnd, setTargetEnd] = useState<HTMLSpanElement>();
	const targetEndRef = useCallback((node: HTMLSpanElement) => {
		setTargetEnd(node);
	}, []);
	const [targetBase, setTargetBase] = useState<HTMLSpanElement>();
	const targetBaseRef = useCallback((node: HTMLSpanElement) => {
		setTargetBase(node);
	}, []);

	const [root, setRoot] = useState<Element>();
	useLayoutEffect(() => {
		if (!view?.dom) {
			setRoot(undefined);
			return;
		}
		const _root = findOverflowScrollParent(view.dom);
		setRoot(_root || undefined);
	}, [view]);

	const {
		isMeasured,
		isTargetAboveViewport,
		isTargetBelowViewport,
		isCardBottomBelowViewport,
		isTargetRightOfViewport,
		isTargetEndAboveViewport,
		isTargetEndBelowViewport,
		isTargetInitiallyOutsideViewport,
	} = useSuggestionCardPositioning({
		root,
		targetStart,
		targetEnd,
		targetBase,
		cardRef,
		id: targetId,
	});

	const shouldScrollUp = isTargetEndAboveViewport && isTargetAboveViewport;
	const shouldScrollDown = isTargetBelowViewport && isTargetEndBelowViewport;

	/**
	 * The target was already out of view when this card was opened, so there is no
	 * card-to-button hand-off to play: show the scroll-back pill at once and never paint the
	 * card. When the target scrolls out of view *while* the card is open, the delayed
	 * slide-away + fade-in below still runs, which is what it was designed for.
	 */
	const shouldSkipScrollBackTransition =
		isTargetInitiallyOutsideViewport && (shouldScrollUp || shouldScrollDown);

	/**
	 * Mutually exclusive positioning modes, one per combination of viewport state:
	 *  - `isPinnedBottomRight`     : fixed to the bottom-right of the viewport
	 *  - `isAnchoredToTargetEnd`   : absolutely anchored to the end of the target
	 *  - `isPinnedTop`             : fixed to the top of the viewport
	 *  - `isPinnedBottom`          : fixed to the bottom of the viewport
	 *  - `isAnchoredToTarget`      : absolutely anchored to the target (default)
	 */
	const isPinnedBottomRight = isTargetRightOfViewport && isTargetEndBelowViewport;
	const isAnchoredToTargetEnd = isTargetRightOfViewport && !isTargetEndBelowViewport;
	const isPinnedTop = !isTargetRightOfViewport && isTargetAboveViewport;
	const isPinnedBottom =
		!isTargetRightOfViewport &&
		!isTargetAboveViewport &&
		(isCardBottomBelowViewport || isTargetBelowViewport);
	const isAnchoredToTarget =
		!isPinnedBottomRight && !isAnchoredToTargetEnd && !isPinnedTop && !isPinnedBottom;

	return (
		<>
			<span
				ref={targetEndRef}
				css={[styles.targetAnchoring]}
				style={{
					positionAnchor: anchorNames.end,
				}}
			/>
			<span
				ref={targetStartRef}
				css={[styles.targetAnchoring]}
				style={{
					positionAnchor: anchorNames.start,
				}}
			/>
			<span
				ref={targetBaseRef}
				css={[styles.targetAnchoring]}
				style={{
					positionAnchor: anchorNames.base,
				}}
			/>
			{onScrollBack && (shouldScrollUp || shouldScrollDown) && (
				<span
					css={[
						styles.cardContainer,
						styles.scrollBackContainer,
						isTargetRightOfViewport && styles.scrollBackRight,
						shouldScrollUp ? styles.scrollBackTop : styles.scrollBackBottom,
						shouldSkipScrollBackTransition
							? styles.scrollBackAnimationQuick
							: styles.scrollBackAnimation,
					]}
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- this is being flagged by precommit hook incorrectly
						...(!isTargetRightOfViewport && {
							insetInlineStart: `anchor(${anchorNames.base} end)`,
						}),
					}}
				>
					<ScrollBackToSuggestionButton
						direction={shouldScrollUp ? 'up' : 'down'}
						onClick={onScrollBack}
					/>
				</span>
			)}
			<span
				css={[
					styles.cardContainer,
					// The fade would animate over the top of `hiddenForScrollBack` — an active
					// animation wins over the declarative opacity — so skip it when the card is
					// never meant to appear.
					isMeasured && !shouldSkipScrollBackTransition && styles.fadeIn,
					isPinnedBottomRight && styles.pinnedBottomRight,
					isAnchoredToTargetEnd && styles.anchoredToTargetEnd,
					isPinnedTop && styles.pinnedTop,
					isPinnedBottom && styles.pinnedBottom,
					isAnchoredToTarget && styles.anchoredToTarget,
					!isMeasured && styles.preMeasurement,
					shouldScrollUp && !shouldSkipScrollBackTransition && styles.slideUp,
					shouldScrollDown && !shouldSkipScrollBackTransition && styles.slideDown,
					shouldSkipScrollBackTransition && styles.hiddenForScrollBack,
				]}
				style={{
					// Modes that aren't right-constrained align the card's inline start with the icon
					// button's inline end. The two that are use a `right` offset from their styles
					// instead, so they must not also get an inline start.
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- this is being flagged by precommit hook incorrectly
					...(!isTargetRightOfViewport && {
						insetInlineStart: `anchor(${anchorNames.base} end)`,
					}),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- this is being flagged by precommit hook incorrectly
					...(isAnchoredToTarget && {
						insetBlockStart: `anchor(${anchorNames.start} start)`,
					}),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- this is being flagged by precommit hook incorrectly
					...(isAnchoredToTargetEnd && {
						insetBlockStart: `calc(anchor(${anchorNames.end} start) + ${token('space.300')})`,
					}),
				}}
			>
				{children}
			</span>
		</>
	);
};
